import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { PlusIcon, DocumentTextIcon, EyeIcon, PencilIcon, TrashIcon, XMarkIcon, DocumentDuplicateIcon } from '@heroicons/react/24/outline';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Recipes = () => {
  const [recipes, setRecipes] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [ingredients, setIngredients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    target_volume: '100',
    target_alcohol_degree: '35',
    target_sugar_percentage: '26',
    notes: ''
  });
  const [recipeComponents, setRecipeComponents] = useState([
    { ingredient_id: '', quantity: '' }
  ]);

  useEffect(() => {
    loadRecipes();
    loadIngredients();
  }, []);

  const loadRecipes = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/recipes`);
      setRecipes(response.data);
    } catch (error) {
      console.error('Errore nel caricamento delle ricette:', error);
      toast.error('Errore nel caricamento delle ricette');
    } finally {
      setLoading(false);
    }
  };

  const loadIngredients = async () => {
    try {
      const response = await axios.get(`${API}/ingredients`);
      setIngredients(response.data);
    } catch (error) {
      console.error('Errore nel caricamento degli ingredienti:', error);
      toast.error('Errore nel caricamento degli ingredienti');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const components = recipeComponents
        .filter(comp => comp.ingredient_id && comp.quantity)
        .map(comp => ({
          ingredient_id: comp.ingredient_id,
          quantity: parseFloat(comp.quantity)
        }));

      if (components.length === 0) {
        toast.error('Aggiungi almeno un ingrediente alla ricetta');
        return;
      }

      const payload = {
        ...formData,
        target_volume: parseFloat(formData.target_volume),
        target_alcohol_degree: parseFloat(formData.target_alcohol_degree),
        target_sugar_percentage: parseFloat(formData.target_sugar_percentage),
        components: components
      };

      if (editingRecipe) {
        await axios.put(`${API}/recipes/${editingRecipe.id}`, payload);
        toast.success('Ricetta aggiornata con successo');
      } else {
        await axios.post(`${API}/recipes`, payload);
        toast.success('Ricetta creata con successo');
      }

      await loadRecipes();
      resetForm();
      setIsModalOpen(false);
    } catch (error) {
      console.error('Errore nel salvataggio:', error);
      toast.error('Errore nel salvataggio della ricetta');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      target_volume: '100',
      target_alcohol_degree: '35',
      target_sugar_percentage: '26',
      notes: ''
    });
    setRecipeComponents([{ ingredient_id: '', quantity: '' }]);
    setEditingRecipe(null);
  };

  const addComponent = () => {
    setRecipeComponents([...recipeComponents, { ingredient_id: '', quantity: '' }]);
  };

  const removeComponent = (index) => {
    const components = recipeComponents.filter((_, i) => i !== index);
    setRecipeComponents(components);
  };

  const updateComponent = (index, field, value) => {
    const components = [...recipeComponents];
    components[index][field] = value;
    setRecipeComponents(components);
  };

  // Calcola la gradazione alcolica reale basata sugli ingredienti
  const calculateActualAlcoholDegree = () => {
    const targetVolume = parseFloat(formData.target_volume) || 0;
    if (targetVolume === 0) return 0;

    let totalAlcoholAnhydrous = 0;

    recipeComponents.forEach(component => {
      if (component.ingredient_id && component.quantity) {
        const ingredient = ingredients.find(ing => ing.id === component.ingredient_id);
        if (ingredient && ingredient.alcohol_percentage > 0) {
          const quantity = parseFloat(component.quantity) || 0;
          // Alcol anidro = quantità × (percentuale alcol / 100)
          totalAlcoholAnhydrous += quantity * (ingredient.alcohol_percentage / 100);
        }
      }
    });

    // Gradazione = (Alcol anidro totale / Volume finale) × 100
    const actualDegree = (totalAlcoholAnhydrous / targetVolume) * 100;
    return actualDegree;
  };

  // Determina il colore e il messaggio del badge in base alla differenza
  const getAlcoholDegreeStatus = () => {
    const target = parseFloat(formData.target_alcohol_degree) || 0;
    const actual = calculateActualAlcoholDegree();
    const difference = actual - target;
    const absDifference = Math.abs(difference);

    let variant = 'default';
    let color = 'text-green-600';
    if (absDifference < 0.5) {
      variant = 'default';
      color = 'text-green-600';
    } else if (absDifference < 2) {
      variant = 'secondary';
      color = 'text-yellow-600';
    } else {
      variant = 'destructive';
      color = 'text-red-600';
    }

    return {
      actual: actual.toFixed(1),
      difference: difference.toFixed(1),
      variant,
      color,
      absDifference
    };
  };

  const handleEdit = (recipe) => {
    setEditingRecipe(recipe);
    setFormData({
      name: recipe.name,
      description: recipe.description,
      target_volume: recipe.target_volume.toString(),
      target_alcohol_degree: recipe.target_alcohol_degree.toString(),
      target_sugar_percentage: recipe.target_sugar_percentage.toString(),
      notes: recipe.notes || ''
    });
    
    // Set recipe components
    const components = recipe.components.map(comp => ({
      ingredient_id: comp.ingredient_id,
      quantity: comp.quantity.toString()
    }));
    setRecipeComponents(components);
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa ricetta?')) {
      return;
    }

    try {
      await axios.delete(`${API}/recipes/${id}`);
      toast.success('Ricetta eliminata con successo');
      await loadRecipes();
    } catch (error) {
      console.error('Errore nell\'eliminazione:', error);
      toast.error('Errore nell\'eliminazione della ricetta');
    }
  };

  const handleDuplicate = async (id) => {
    try {
      await axios.post(`${API}/recipes/${id}/duplicate`);
      toast.success('Ricetta duplicata con successo');
      await loadRecipes();
    } catch (error) {
      console.error('Errore nella duplicazione:', error);
      toast.error('Errore nella duplicazione della ricetta');
    }
  };

  const getIngredientName = (ingredientId) => {
    const ingredient = ingredients.find(ing => ing.id === ingredientId);
    return ingredient ? ingredient.name : 'Ingrediente non trovato';
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 3,
      maximumFractionDigits: 3
    }).format(value);
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="recipes-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestione Ricette</h1>
          <p className="text-gray-600">Gestisci le ricette del liquido per i prodotti</p>
        </div>
        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm} data-testid="add-recipe-btn">
              <PlusIcon className="w-4 h-4 mr-2" />
              Nuova Ricetta
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingRecipe ? 'Modifica Ricetta' : 'Nuova Ricetta'}
              </DialogTitle>
              <DialogDescription>
                Crea una ricetta definendo gli ingredienti e le quantità. I calcoli di costo e alcol anidro saranno automatici.
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nome Ricetta *</Label>
                  <Input
                    id="name"
                    data-testid="recipe-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Es. Amaro V 35°"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Descrizione</Label>
                  <Input
                    id="description"
                    data-testid="recipe-description-input"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Es. Amaro tipo del capo"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="target_volume">Volume Target (L) *</Label>
                  <Input
                    id="target_volume"
                    data-testid="recipe-volume-input"
                    type="number"
                    step="0.1"
                    value={formData.target_volume}
                    onChange={(e) => setFormData({...formData, target_volume: e.target.value})}
                    placeholder="100"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="target_alcohol_degree">Gradi Target (°) *</Label>
                  <Input
                    id="target_alcohol_degree"
                    data-testid="recipe-alcohol-input"
                    type="number"
                    step="0.1"
                    value={formData.target_alcohol_degree}
                    onChange={(e) => setFormData({...formData, target_alcohol_degree: e.target.value})}
                    placeholder="35"
                    required
                  />
                  {/* Indicatore gradazione calcolata */}
                  {formData.target_volume && recipeComponents.some(c => c.ingredient_id && c.quantity) && (() => {
                    const status = getAlcoholDegreeStatus();
                    return (
                      <div className="mt-2 p-3 bg-gray-50 rounded-lg border">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Gradazione Calcolata:</span>
                          <div className="flex items-center gap-2">
                            <span className={`text-lg font-bold ${status.color}`}>
                              {status.actual}°
                            </span>
                            {status.absDifference >= 0.5 && (
                              <Badge variant={status.variant} className="text-xs">
                                {status.difference > 0 ? '+' : ''}{status.difference}°
                              </Badge>
                            )}
                          </div>
                        </div>
                        {status.absDifference < 0.5 && (
                          <p className="text-xs text-green-600 mt-1">✓ Gradazione corretta</p>
                        )}
                        {status.absDifference >= 0.5 && status.absDifference < 2 && (
                          <p className="text-xs text-yellow-600 mt-1">⚠ Leggera differenza dalla gradazione target</p>
                        )}
                        {status.absDifference >= 2 && (
                          <p className="text-xs text-red-600 mt-1">⚠ Differenza significativa dalla gradazione target</p>
                        )}
                      </div>
                    );
                  })()}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="target_sugar_percentage">% Zucchero *</Label>
                  <Input
                    id="target_sugar_percentage"
                    data-testid="recipe-sugar-input"
                    type="number"
                    step="0.1"
                    value={formData.target_sugar_percentage}
                    onChange={(e) => setFormData({...formData, target_sugar_percentage: e.target.value})}
                    placeholder="26"
                    required
                  />
                </div>
              </div>

              <div className="border-t pt-4">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="text-sm font-medium text-gray-900">Ingredienti</h4>
                  <Button type="button" variant="outline" size="sm" onClick={addComponent}>
                    <PlusIcon className="w-4 h-4 mr-2" />
                    Aggiungi Ingrediente
                  </Button>
                </div>
                
                <div className="space-y-3">
                  {recipeComponents.map((component, index) => (
                    <div key={index} className="flex gap-4 items-end">
                      <div className="flex-1">
                        <Label>Ingrediente *</Label>
                        <Select
                          value={component.ingredient_id}
                          onValueChange={(value) => updateComponent(index, 'ingredient_id', value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Seleziona ingrediente" />
                          </SelectTrigger>
                          <SelectContent>
                            {ingredients.map((ingredient) => (
                              <SelectItem key={ingredient.id} value={ingredient.id}>
                                {ingredient.name} ({ingredient.code})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="w-32">
                        <Label>Quantità *</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={component.quantity}
                          onChange={(e) => updateComponent(index, 'quantity', e.target.value)}
                          placeholder="0.00"
                        />
                      </div>
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={() => removeComponent(index)}
                        disabled={recipeComponents.length === 1}
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Note</Label>
                <Input
                  id="notes"
                  data-testid="recipe-notes-input"
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Note aggiuntive sulla ricetta"
                />
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsModalOpen(false)}
                  data-testid="cancel-recipe-btn"
                >
                  Annulla
                </Button>
                <Button type="submit" data-testid="save-recipe-btn">
                  {editingRecipe ? 'Aggiorna' : 'Salva'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search Bar */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Input
              type="text"
              placeholder="🔍 Cerca per nome..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-4"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            )}
          </div>
          {searchQuery && (
            <p className="text-sm text-gray-500 mt-2">
              {recipes.filter(recipe => 
                recipe.name.toLowerCase().includes(searchQuery.toLowerCase())
              ).length} risultati trovati
            </p>
          )}
        </CardContent>
      </Card>

      {/* Recipes List */}
      {recipes.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <DocumentTextIcon className="w-16 h-16 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Nessuna ricetta</h3>
            <p className="text-gray-500 text-center mb-6">
              Le ricette permettono di definire la composizione del liquido per i tuoi prodotti.
              <br />
              Inizia creando la tua prima ricetta utilizzando gli ingredienti già definiti.
            </p>
            <div className="space-y-4 text-sm text-gray-600">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-medium text-blue-900 mb-2">Esempio di Ricetta (per 100L a 35°):</h4>
                <ul className="space-y-1 text-blue-800">
                  <li>• Alcool 96.3: 28.10 kg → 34.91L alcool anidro</li>
                  <li>• Acqua: 49.59 kg</li>
                  <li>• Zucchero: 26.00 kg</li>
                  <li>• Aroma Amaro: 0.96 kg (69° TAV)</li>
                  <li>• Infuso Felsina: 0.96 kg (50° TAV)</li>
                </ul>
                <p className="mt-2 text-xs text-blue-700">
                  <strong>Totale alcool anidro:</strong> 35.00L • <strong>Contrazione:</strong> -2.9L
                </p>
              </div>
            </div>
            <Button className="mt-4" onClick={() => setIsModalOpen(true)}>
              <PlusIcon className="w-4 h-4 mr-2" />
              Crea prima ricetta
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {recipes
            .filter(recipe => 
              recipe.name.toLowerCase().includes(searchQuery.toLowerCase())
            )
            .map((recipe) => (
            <Card key={recipe.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <CardTitle className="text-lg">{recipe.name}</CardTitle>
                    <CardDescription>
                      {recipe.target_volume}L • {recipe.target_alcohol_degree}° • {recipe.target_sugar_percentage}% zucchero
                      <br />
                      Alcol anidro: {recipe.total_alcohol_anhydrous.toFixed(2)}L • Costo: {formatCurrency(recipe.total_cost)}
                      <br />
                      Volume finale: {recipe.final_volume.toFixed(2)}L (contrazione: -{recipe.contraction.toFixed(2)}L)
                    </CardDescription>
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(recipe)}
                      data-testid={`edit-recipe-${recipe.id}`}
                    >
                      <PencilIcon className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDuplicate(recipe.id)}
                      data-testid={`duplicate-recipe-${recipe.id}`}
                      title="Duplica ricetta"
                    >
                      <DocumentDuplicateIcon className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDelete(recipe.id)}
                      data-testid={`delete-recipe-${recipe.id}`}
                    >
                      <TrashIcon className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-gray-900">Ingredienti:</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                    {recipe.components.map((component, index) => (
                      <div key={index} className="flex justify-between">
                        <span>{component.ingredient_name}:</span>
                        <span>{component.quantity} {component.unit_of_measure} - {formatCurrency(component.cost)}</span>
                      </div>
                    ))}
                  </div>
                </div>
                {recipe.notes && (
                  <div className="mt-3 pt-3 border-t">
                    <p className="text-sm text-gray-600">{recipe.notes}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Recipes;